import type { JSX } from 'react';

import { $isCodeHighlightNode } from '@lexical/code';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isTableCellNode, $isTableNode, $isTableRowNode, $isTableSelection } from '@lexical/table';
import { mergeRegister } from '@lexical/utils';
import {
  $getNodeByKey,
  $getSelection,
  $isParagraphNode,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_LOW,
  FORMAT_TEXT_COMMAND,
  getDOMSelection,
  LexicalEditor,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ActionIcon, Group, Stack } from '@mantine/core';
import { IconBold, IconItalic, IconStrikethrough, IconUnderline } from '@tabler/icons-react';
import { TableSelectionInfo } from '../types';
import { getDOMRangeRect } from '../utils/getDOMRangeRect';
import { getSelectedNode } from '../utils/getSelectedNode';
import { setFloatingElemPosition } from '../utils/setFloatingElemPosition';
import { isHtmlOrigin } from '../utils/tableOrigin';
import BackdropBlur from 'src/UI/common/BackdropBlur';
import { SiestaInlineInput } from './SiestaInlineInput';

const HIGHLIGHT_NAME = 'editor-preserved-selection';

function clearPreservedHighlight() {
  CSS.highlights?.delete(HIGHLIGHT_NAME);
}

function preserveSelectionHighlight(rootElement: HTMLElement) {
  if (!CSS.highlights) return;
  const sel = document.getSelection();
  if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
  if (!rootElement.contains(sel.anchorNode)) return;

  const range = sel.getRangeAt(0);
  const highlight = new Highlight(range);
  CSS.highlights.set(HIGHLIGHT_NAME, highlight);
}

type TableSelectionCells = {
  topLeftKey: string;
  bottomRightKey: string;
};

function TextFormatFloatingToolbar({
  editor,
  anchorElem,
  isBold,
  isItalic,
  isUnderline,
  isStrikethrough,
  enableInlineEditing,
  toolbarRef,
  isLoading,
  onLoadingChange,
  tableSelectionInfo,
  tableSelectionCells,
}: {
  editor: LexicalEditor;
  anchorElem: HTMLElement;
  isBold: boolean;
  isItalic: boolean;
  isStrikethrough: boolean;
  isUnderline: boolean;
  enableInlineEditing: boolean;
  toolbarRef: (ref: HTMLElement | null) => void;
  isLoading: boolean;
  onLoadingChange: (isLoading: boolean) => void;
  tableSelectionInfo: TableSelectionInfo | null;
  tableSelectionCells: TableSelectionCells | null;
}): JSX.Element {
  const popupCharStylesEditorRef = useRef<HTMLElement | null>(null);

  function mouseMoveListener(e: MouseEvent) {
    if (popupCharStylesEditorRef?.current && (e.buttons === 1 || e.buttons === 3)) {
      if (popupCharStylesEditorRef.current.style.pointerEvents !== 'none') {
        const x = e.clientX;
        const y = e.clientY;
        const elementUnderMouse = document.elementFromPoint(x, y);

        if (!popupCharStylesEditorRef.current.contains(elementUnderMouse)) {
          popupCharStylesEditorRef.current.style.pointerEvents = 'none';
        }
      }
    }
  }
  function mouseUpListener() {
    if (popupCharStylesEditorRef?.current) {
      if (popupCharStylesEditorRef.current.style.pointerEvents !== 'auto') {
        popupCharStylesEditorRef.current.style.pointerEvents = 'auto';
      }
    }
  }

  useEffect(() => {
    if (popupCharStylesEditorRef?.current) {
      document.addEventListener('mousemove', mouseMoveListener);
      document.addEventListener('mouseup', mouseUpListener);

      return () => {
        document.removeEventListener('mousemove', mouseMoveListener);
        document.removeEventListener('mouseup', mouseUpListener);
      };
    }
  }, [popupCharStylesEditorRef]);

  useEffect(() => {
    const rootElement = editor.getRootElement();
    if (!rootElement) return;

    const handleEditorFocusOut = (e: FocusEvent) => {
      const relatedTarget = e.relatedTarget as Node | null;
      if (relatedTarget && popupCharStylesEditorRef.current?.contains(relatedTarget)) {
        preserveSelectionHighlight(rootElement);
      } else {
        clearPreservedHighlight();
      }
    };

    const handleEditorFocusIn = () => {
      clearPreservedHighlight();
    };

    rootElement.addEventListener('focusout', handleEditorFocusOut);
    rootElement.addEventListener('focusin', handleEditorFocusIn);

    return () => {
      rootElement.removeEventListener('focusout', handleEditorFocusOut);
      rootElement.removeEventListener('focusin', handleEditorFocusIn);
      clearPreservedHighlight();
    };
  }, [editor]);

  const $updateTextFormatFloatingToolbar = useCallback(() => {
    const popupCharStylesEditorElem = popupCharStylesEditorRef.current;
    if (popupCharStylesEditorElem === null) {
      return;
    }

    // Table selection positioning: use corner cell bounding rects
    if (tableSelectionCells) {
      const topLeftDOM = editor.getElementByKey(tableSelectionCells.topLeftKey);
      const bottomRightDOM = editor.getElementByKey(tableSelectionCells.bottomRightKey);
      if (topLeftDOM && bottomRightDOM) {
        const tl = topLeftDOM.getBoundingClientRect();
        const br = bottomRightDOM.getBoundingClientRect();
        const unionRect = new DOMRect(tl.left, tl.top, br.right - tl.left, br.bottom - tl.top);
        setFloatingElemPosition(unionRect, popupCharStylesEditorElem, anchorElem);
      }
      return;
    }

    // Range selection positioning: use native selection rect
    const nativeSelection = getDOMSelection(editor._window);
    const rootElement = editor.getRootElement();
    if (
      nativeSelection !== null &&
      !nativeSelection.isCollapsed &&
      rootElement !== null &&
      rootElement.contains(nativeSelection.anchorNode)
    ) {
      const rangeRect = getDOMRangeRect(nativeSelection, rootElement);
      setFloatingElemPosition(rangeRect, popupCharStylesEditorElem, anchorElem);
    }
  }, [editor, anchorElem, tableSelectionCells]);

  useEffect(() => {
    const scrollerElem = anchorElem.parentElement;

    const update = () => {
      editor.getEditorState().read(() => {
        $updateTextFormatFloatingToolbar();
      });
    };

    window.addEventListener('resize', update);
    if (scrollerElem) {
      scrollerElem.addEventListener('scroll', update);
    }

    return () => {
      window.removeEventListener('resize', update);
      if (scrollerElem) {
        scrollerElem.removeEventListener('scroll', update);
      }
    };
  }, [editor, $updateTextFormatFloatingToolbar, anchorElem]);

  useEffect(() => {
    editor.getEditorState().read(() => {
      $updateTextFormatFloatingToolbar();
    });
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateTextFormatFloatingToolbar();
        });
      }),

      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          $updateTextFormatFloatingToolbar();
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    );
  }, [editor, $updateTextFormatFloatingToolbar]);

  return (
    <BackdropBlur
      size="xxl"
      data-floating-toolbar
      ref={ref => {
        popupCharStylesEditorRef.current = ref;
        toolbarRef(ref);
      }}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: 10,
        width: enableInlineEditing ? 364 : undefined,
        borderRadius:
          'var(--mantine-spacing-xxs) var(--mantine-spacing-lg) var(--mantine-spacing-lg) var(--mantine-spacing-lg)',
        border: '1.5px solid #FF771C1A',
        backgroundColor: 'rgba(255, 255, 255, 0.5)',
      }}
    >
      {editor.isEditable() && (
        <Stack gap="xs" px="sm" py="xs">
          {!isLoading && !tableSelectionInfo && (
            <Group gap="xxxs" wrap="nowrap">
              <ActionIcon
                variant="subtle"
                color={isBold ? 'brand.6' : 'black'}
                onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}
                title="Bold"
                aria-label="Format text as bold"
                size={28}
              >
                <IconBold size={18} />
              </ActionIcon>
              <ActionIcon
                variant="subtle"
                color={isItalic ? 'brand.6' : 'black'}
                onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')}
                title="Italic"
                aria-label="Format text as italics"
                size={28}
              >
                <IconItalic size={18} />
              </ActionIcon>
              <ActionIcon
                variant="subtle"
                color={isUnderline ? 'brand.6' : 'black'}
                onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')}
                title="Underline"
                aria-label="Format text to underlined"
                size={28}
              >
                <IconUnderline size={18} />
              </ActionIcon>
              <ActionIcon
                variant="subtle"
                color={isStrikethrough ? 'brand.6' : 'black'}
                onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough')}
                title="Strikethrough"
                aria-label="Format text with a strikethrough"
                size={28}
              >
                <IconStrikethrough size={18} />
              </ActionIcon>
            </Group>
          )}

          {enableInlineEditing && (
            <SiestaInlineInput
              editor={editor}
              onLoadingChange={onLoadingChange}
              isLoading={isLoading}
              tableSelectionInfo={tableSelectionInfo}
            />
          )}
        </Stack>
      )}
    </BackdropBlur>
  );
}

function useFloatingTextFormatToolbar(
  editor: LexicalEditor,
  anchorElem: HTMLElement,
  enableInlineEditing: boolean,
  externalIsLoading?: boolean,
  externalOnLoadingChange?: (loading: boolean) => void,
  hasInlineEdits?: boolean,
): JSX.Element | null {
  const [isText, setIsText] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [internalIsLoading, setInternalIsLoading] = useState(false);
  const isLoadingRef = useRef(externalIsLoading ?? false);
  const toolbarRef = useRef<HTMLElement | null>(null);
  const [tableSelectionInfo, setTableSelectionInfo] = useState<TableSelectionInfo | null>(null);
  const [tableSelectionCells, setTableSelectionCells] = useState<TableSelectionCells | null>(null);

  const isLoading = externalIsLoading ?? internalIsLoading;

  const setIsLoading = useCallback(
    (loading: boolean) => {
      isLoadingRef.current = loading;
      if (externalOnLoadingChange) {
        externalOnLoadingChange(loading);
      } else {
        setInternalIsLoading(loading);
      }
    },
    [externalOnLoadingChange],
  );

  const handleLoadingChange = useCallback(
    (loading: boolean) => {
      setIsLoading(loading);
      if (!loading) {
        setIsText(false);
      }
    },
    [setIsLoading],
  );

  const updatePopup = useCallback(() => {
    const shouldKeepVisible = () => {
      return toolbarRef.current?.contains(document.activeElement) || isLoadingRef.current;
    };

    editor.getEditorState().read(() => {
      if (editor.isComposing()) {
        return;
      }
      const selection = $getSelection();

      // Table selection: show toolbar with only Siesta AI input
      if ($isTableSelection(selection)) {
        const shape = selection.getShape();
        const tableNode = $getNodeByKey(selection.tableKey);
        if (tableNode && $isTableNode(tableNode) && !isHtmlOrigin(tableNode)) {
          const rows = tableNode.getChildren().filter($isTableRowNode);
          const topLeftCell = rows[shape.fromY]?.getChildAtIndex(shape.fromX);
          const bottomRightCell = rows[shape.toY]?.getChildAtIndex(shape.toX);

          if (
            topLeftCell &&
            bottomRightCell &&
            $isTableCellNode(topLeftCell) &&
            $isTableCellNode(bottomRightCell)
          ) {
            setTableSelectionCells({
              topLeftKey: topLeftCell.getKey(),
              bottomRightKey: bottomRightCell.getKey(),
            });
            setTableSelectionInfo({
              tableNodeKey: selection.tableKey,
              startRow: shape.fromY,
              endRow: shape.toY,
              startCol: shape.fromX,
              endCol: shape.toX,
            });
            setIsText(true);
            return;
          }
        }
      }

      const nativeSelection = getDOMSelection(editor._window);
      const rootElement = editor.getRootElement();

      if (
        nativeSelection !== null &&
        (!$isRangeSelection(selection) ||
          rootElement === null ||
          !rootElement.contains(nativeSelection.anchorNode))
      ) {
        if (!shouldKeepVisible()) {
          setIsText(false);
          setTableSelectionInfo(null);
          setTableSelectionCells(null);
        }
        return;
      }

      if (!$isRangeSelection(selection)) {
        return;
      }

      // Clear table state when in range selection mode
      setTableSelectionInfo(null);
      setTableSelectionCells(null);

      const node = getSelectedNode(selection);

      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));
      setIsStrikethrough(selection.hasFormat('strikethrough'));

      if (!$isCodeHighlightNode(selection.anchor.getNode()) && selection.getTextContent() !== '') {
        setIsText($isTextNode(node) || $isParagraphNode(node));
      } else {
        if (!shouldKeepVisible()) {
          setIsText(false);
        }
      }

      const rawTextContent = selection.getTextContent().replace(/\n/g, '');
      if (!selection.isCollapsed() && rawTextContent === '') {
        if (!shouldKeepVisible()) {
          setIsText(false);
        }
        return;
      }
    });
  }, [editor]);

  useEffect(() => {
    document.addEventListener('selectionchange', updatePopup);
    return () => {
      document.removeEventListener('selectionchange', updatePopup);
    };
  }, [updatePopup]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(() => {
        updatePopup();
      }),
      editor.registerRootListener(() => {
        if (editor.getRootElement() === null) {
          setIsText(false);
        }
      }),
    );
  }, [editor, updatePopup]);

  if ((!isText && !isLoading) || hasInlineEdits) {
    return null;
  }

  return createPortal(
    <TextFormatFloatingToolbar
      editor={editor}
      anchorElem={anchorElem}
      isBold={isBold}
      isItalic={isItalic}
      isStrikethrough={isStrikethrough}
      isUnderline={isUnderline}
      enableInlineEditing={enableInlineEditing}
      toolbarRef={ref => {
        toolbarRef.current = ref;
      }}
      isLoading={isLoading}
      onLoadingChange={handleLoadingChange}
      tableSelectionInfo={tableSelectionInfo}
      tableSelectionCells={tableSelectionCells}
    />,
    anchorElem,
  );
}

export default function FloatingTextFormatToolbarPlugin({
  anchorElem = document.body,
  enableInlineEditing = false,
  isInlineEditLoading,
  onInlineEditLoadingChange,
  hasInlineEdits = false,
}: {
  anchorElem?: HTMLElement;
  enableInlineEditing?: boolean;
  isInlineEditLoading?: boolean;
  onInlineEditLoadingChange?: (loading: boolean) => void;
  hasInlineEdits?: boolean;
}): JSX.Element | null {
  const [editor] = useLexicalComposerContext();
  return useFloatingTextFormatToolbar(
    editor,
    anchorElem,
    enableInlineEditing,
    isInlineEditLoading,
    onInlineEditLoadingChange,
    hasInlineEdits,
  );
}
